import TemplateDto from '@/services/skills/Dto/TemplateDto';
import PageTemplateDto from '@/services/skills/Dto/PageTemplateDto';
import TemplateRequestDto from '@/services/skills/Dto/TemplateRequestDto';
import TemplatesApiRepository from '@/services/skills/TemplatesApiRepository';
import { v4 as uuidv4 } from 'uuid';

const FLOW_TYPE = 'NEW';
const START_CONVERSATION_SKILL_ID = '21e4c0a2-8aac-4703-908d-356def6b35bc';
const CATEGORY = 'UTILITY';
/** Sample image URL for Meta template approval (not shown to end users) */
const SAMPLE_HEADER_IMAGE_URL =
    'https://firebasestorage.googleapis.com/v0/b/lixsa-prod.appspot.com/o/public_images%2Fimg_preview.png?alt=media&token=8a4f624d-2431-413e-8739-fae322990b45';
/** Sample video placeholder URL for Meta template approval */
const SAMPLE_HEADER_VIDEO_URL =
    'https://firebasestorage.googleapis.com/v0/b/lixsa-prod.appspot.com/o/public_images%2Fvideo_preview.png?alt=media&token=d7e353a1-dcce-473e-81a7-8a7b75750a55';

/** @returns {object} Initial state for the startConversation module */
const state = () => ({
    templatesList: null,
    currentLanguage: 'en',
    currentTemplate: null,
    availableLanguages: [],
});

/**
 * Formats a language string to a consistent format (e.g., 'en_US').
 * @param {string} language - The language string to format.
 * @returns {string} The formatted language string.
 */
const formatLanguage = (language) => {
    if (!language) return '';
    const [base, region] = language.split('_');
    return region ? `${base.toLowerCase()}_${region.toUpperCase()}` : base.toLowerCase();
};

const getters = {
    getCurrentTemplate: (state) => state.currentTemplate,
    getCurrentLanguage: (state) => state.currentLanguage,
    getAvailableLanguages: (state) => state.availableLanguages,
    currentTemplateIsSavedInTemplatesList: (state) => {
        if (!state.currentTemplate) return false;

        return state.templatesList.content.some((template) => template.uuid === state.currentTemplate.uuid);
    },
    getCurrentTemplateStatus: (state) => {
        if (!state.currentTemplate) return null;

        return state.currentTemplate.status || 'NEW';
    },
};

const mutations = {
    SET_TEMPLATE_LIST(state, templatesList) {
        state.templatesList = templatesList;
    },
    SET_CURRENT_LANGUAGE(state, language) {
        state.currentLanguage = language;
    },
    SET_AVAILABLE_LANGUAGES(state, languages) {
        state.availableLanguages = languages;
    },
    SET_CURRENT_TEMPLATE(state, template) {
        state.currentTemplate = template;
    },
};

const actions = {
    /**
     * Fetches the list of templates for the Start Conversation flow type.
     * @param {object} context - Vuex action context.
     * @returns {Promise<object>} The fetched templates.
     */
    async fetchTemplatesList({ commit }) {
        try {
            const templates = await TemplatesApiRepository.getTemplates(FLOW_TYPE);
            commit('SET_TEMPLATE_LIST', templates);
            return templates;
        } catch (error) {
            console.error('Error fetching templates:', error);
            throw error;
        }
    },

    /**
     * Fetches the current template matching the current language, or falls back to the default.
     * @param {object} context - Vuex action context.
     */
    async fetchCurrentTemplate({ commit, state, dispatch }) {
        const foundTemplate = state.templatesList?.content?.find(
            (template) => formatLanguage(template.language) === formatLanguage(state.currentLanguage),
        );
        if (foundTemplate) {
            const existingTemplate = new TemplateDto(foundTemplate);
            commit('SET_CURRENT_TEMPLATE', existingTemplate);
            return;
        }

        try {
            await dispatch('fetchDefaultTemplate');
        } catch (error) {
            console.error('Error fetching current template:', error);
        }
    },

    /**
     * Fetches the default template variables from the API.
     * @param {object} context - Vuex action context.
     */
    async fetchDefaultTemplate({ commit, state }) {
        try {
            const templateVariables = await TemplatesApiRepository.getTemplateVariables(
                START_CONVERSATION_SKILL_ID,
                FLOW_TYPE,
                state.currentLanguage,
            );
            const existingTemplate = new TemplateDto.Builder()
                .withUuid(templateVariables.uuid)
                .withTemplateType(templateVariables.templateType)
                .withFlowType(templateVariables.flowType)
                .withSkillId(templateVariables.skillId)
                .withVariables([templateVariables])
                .withBody(templateVariables.contentExample)
                .withLanguage(templateVariables.language)
                .withDateCreated(templateVariables.dateCreated)
                .withDateUpdated(templateVariables.dateUpdated)
                .build();

            commit('SET_CURRENT_TEMPLATE', existingTemplate);
        } catch (error) {
            console.error('Error fetching default template:', error);
            throw error;
        }
    },

    /**
     * Fetches the list of available languages for templates.
     * @param {object} context - Vuex action context.
     */
    async fetchAvailableLanguages({ commit }) {
        try {
            const languages = await TemplatesApiRepository.getAvailableLanguages();
            commit('SET_AVAILABLE_LANGUAGES', languages);
        } catch (error) {
            console.error('Error fetching available languages:', error);
            throw error;
        }
    },

    /**
     * Sets the current language to 'en' (no Shopify dependency).
     * @param {object} context - Vuex action context.
     */
    async fetchCurrentLanguage({ commit }) {
        commit('SET_CURRENT_LANGUAGE', 'en');
    },

    /**
     * Creates or updates a template for the Start Conversation skill.
     * @param {object} context - Vuex action context.
     * @param {object} payload - The template payload.
     * @param {string} payload.templateContent - The template body content.
     * @param {string} [payload.templateName] - Optional template name override.
     * @param {string} payload.accountId - The account ID.
     */

    async updateTemplate(
        { state, getters },
        { templateContent, templateName, accountId, headerType, businessAccountId },
    ) {
        try {
            const isEditing = getters.currentTemplateIsSavedInTemplatesList;

            if (isEditing && state.currentTemplate) {
                // Update template
                const templateRequest = new TemplateRequestDto(state.currentTemplate);
                templateRequest.body = templateContent;
                if (templateName) {
                    templateRequest.template_name = templateName;
                }
                // Set media header type and sample URL
                templateRequest.type = headerType || 'TEXT';
                if (headerType === 'IMAGE') {
                    templateRequest.header = SAMPLE_HEADER_IMAGE_URL;
                } else if (headerType === 'VIDEO') {
                    templateRequest.header = SAMPLE_HEADER_VIDEO_URL;
                } else {
                    templateRequest.header = null;
                }
                await TemplatesApiRepository.updateTemplate(state.currentTemplate.uuid, templateRequest);
            } else {
                // Create template
                const resolvedHeaderUrl =
                    headerType === 'IMAGE'
                        ? SAMPLE_HEADER_IMAGE_URL
                        : headerType === 'VIDEO'
                          ? SAMPLE_HEADER_VIDEO_URL
                          : null;
                const templateRequest = new TemplateRequestDto.Builder()
                    .withUuid(uuidv4())
                    .withAccountId(accountId)
                    .withBody(templateContent)
                    .withHeader(resolvedHeaderUrl)
                    .withLanguage(formatLanguage(state.currentLanguage))
                    .withTemplateName(templateName || 'start_conversation')
                    .withCategory(CATEGORY)
                    .withType(headerType || 'TEXT')
                    .withTemplateType('FLOW')
                    .withFlowType(FLOW_TYPE)
                    .withSkillId(START_CONVERSATION_SKILL_ID)
                    .withChannelType('WHATSAPP')
                    .withBusinessAccountId(businessAccountId)
                    .build();
                await TemplatesApiRepository.createTemplate(templateRequest);
            }
        } catch (error) {
            console.error('Error updating template:', error);
            throw error;
        }
    },

    /**
     * Deletes a template by UUID with optimistic UI update.
     * @param {object} context - Vuex action context.
     * @param {string} templateUuid - The UUID of the template to delete.
     */
    async deleteTemplate({ commit, state }, templateUuid) {
        try {
            const foundTemplate = state.templatesList.content.find((template) => template.uuid === templateUuid);
            if (foundTemplate) {
                const templateRequest = new TemplateRequestDto(foundTemplate);
                await TemplatesApiRepository.deleteTemplate(templateUuid, templateRequest);

                // Optimistic UI update: Remove from local list immediately
                const updatedContent = state.templatesList.content.filter((t) => t.uuid !== templateUuid);
                const updatedList = new PageTemplateDto({
                    ...state.templatesList,
                    content: updatedContent,
                    numberOfElements: updatedContent.length,
                });
                commit('SET_TEMPLATE_LIST', updatedList);
            }
        } catch (error) {
            console.error('Error deleting template:', error);
            throw error;
        }
    },

    /**
     * Sets the current language.
     * @param {object} context - Vuex action context.
     * @param {string} language - The language tag to set.
     */
    setCurrentLanguage({ commit }, language) {
        commit('SET_CURRENT_LANGUAGE', formatLanguage(language));
    },

    /**
     * Selects a template and sets it as the current template.
     * @param {object} context - Vuex action context.
     * @param {object} template - The template object to select.
     */
    selectTemplate({ commit }, template) {
        commit('SET_CURRENT_TEMPLATE', new TemplateDto(template));
    },

    /**
     * Subscribes to WebSocket template update events.
     * @param {object} context - Vuex action context.
     */
    async subscribeToTemplateUpdates({ rootState, dispatch }) {
        const topic = `/account/${rootState.user.account.uuid}/template`;
        try {
            await dispatch(
                'websocket/subscribe',
                { topic, callback: (data) => dispatch('handleTemplateEvent', data) },
                { root: true },
            );
        } catch (error) {
            console.error(`Failed to subscribe to ${topic}:`, error);
        }
    },

    /**
     * Unsubscribes from WebSocket template update events.
     * @param {object} context - Vuex action context.
     */
    unsubscribeFromTemplateUpdates({ rootState, dispatch }) {
        const topic = `/account/${rootState.user.account.uuid}/template`;
        dispatch('websocket/unsubscribe', { topic }, { root: true });
    },

    /**
     * Handles incoming WebSocket template events (TemplateUpdated, TemplateScoringUpdated).
     * @param {object} context - Vuex action context.
     * @param {object} event - The WebSocket event data.
     */
    handleTemplateEvent({ commit, state, dispatch }, event) {
        if (!event || !event.data) return;

        // Parse JSON string if it's a string
        const eventData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        const templateSource = eventData;

        // Create template using builder pattern to ensure proper object construction
        const template = new TemplateDto.Builder()
            .withUuid(templateSource.uuid)
            .withAccountId(templateSource.accountId || templateSource.account_id)
            .withBody(templateSource.body)
            .withHeader(templateSource.header)
            .withChannelType(templateSource.channelType || templateSource.channel_type)
            .withTemplateName(templateSource.templateName || templateSource.template_name)
            .withLanguage(templateSource.language)
            .withCategory(templateSource.category)
            .withStatus(templateSource.status)
            .withScore(templateSource.score)
            .withType(templateSource.type)
            .withTemplateType(templateSource.templateType || templateSource.template_type)
            .withFlowType(templateSource.flowType || templateSource.flow_type)
            .withSkillId(templateSource.skillId || templateSource.skill_id)
            .withInternalTemplateId(templateSource.internalTemplateId || templateSource.internal_template_id)
            .withTemplateParameters(templateSource.templateParameters || templateSource.template_parameters)
            .withExtraParameters(templateSource.extraParameters || templateSource.extra_parameters)
            .withIssues(templateSource.issues)
            .withModifyBy(templateSource.modifyBy || templateSource.modify_by)
            .withBusinessAccountId(templateSource.businessAccountId || templateSource.business_account_id)
            .withDateCreated(templateSource.dateCreated || templateSource.date_created)
            .withDateUpdated(templateSource.dateUpdated || templateSource.date_updated)
            .withVariables(templateSource.variables || [])
            .build();

        if (!state.templatesList) return;

        // Create a shallow copy of the templates list and its content array to ensure reactivity
        const updatedTemplatesList = { ...state.templatesList };
        updatedTemplatesList.content = [...state.templatesList.content];

        const templateIndex = updatedTemplatesList.content.findIndex((t) => t.uuid === template.uuid);

        switch (event.name) {
            case 'TemplateUpdated':
                // Find and update the template in the list
                if (templateIndex !== -1) {
                    updatedTemplatesList.content[templateIndex] = template;
                    commit('SET_TEMPLATE_LIST', updatedTemplatesList);

                    // If current template is the updated one, update it directly
                    if (state.currentTemplate?.uuid === template.uuid) {
                        dispatch('fetchCurrentTemplate');
                    }
                }
                break;
            case 'TemplateScoringUpdated':
                if (templateIndex !== -1) {
                    const existingTemplate = updatedTemplatesList.content[templateIndex];
                    updatedTemplatesList.content[templateIndex] = {
                        ...existingTemplate,
                        score: template.score,
                    };

                    commit('SET_TEMPLATE_LIST', updatedTemplatesList);

                    if (state.currentTemplate?.uuid === template.uuid) {
                        dispatch('fetchCurrentTemplate');
                    }
                }
                break;
            default:
                console.warn(`Unhandled template event: ${event.name}`);
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
